<template>
  <div class="autocomplete-wrapper" ref="wrapper">
    <b-form-input
      v-model="query"
      @input="onSearch"
      @focus="!minQueryLength && !query.length ? onSearch() : (showDropdown = true)"
      @blur="showSelected"
      @keydown.down.prevent="highlightNext"
      @keydown.up.prevent="highlightPrev"
      @keydown.enter.prevent="selectHighlighted"
      :placeholder="placeholder"
      :state="state"
      ref="input"
      autocomplete="off"
      v-b-popover.click.blur.top="tooltip"
    />

    <ul v-if="showDropdown && options.length" class="autocomplete-list">
      <li
        v-for="(option, index) in options"
        :key="option.value"
        :ref="'option-' + index"
        :class="{ highlighted: index === highlightedIndex }"
        @click="selectOption(option)"
      >
        {{ option.name }}
      </li>
    </ul>
  </div>
</template>

<script>
import Vue from 'vue'

const AutocompleteInput = Vue.component('autocomplete-input', {
  name: '',
  props: {
    fetchOptions: {
      type: Function,
      required: false,
      default: () => () => {},
    },
    value: Object,
    state: Boolean,
    placeholder: {
      type: String,
      require: false,
      default: '',
    },
    minQueryLength: {
      type: Number,
      required: false,
      default: 2,
    },
    tooltip: {
      type: Object, // title / text
      required: false,
    },
  },
  data() {
    return {
      query: '',
      options: [],
      showDropdown: false,
      debounceTimeout: null,
      highlightedIndex: -1,
      requestedQuery: null,
    }
  },
  watch: {
    value(newVal) {
      this.query = newVal?.name ?? ''
      if (!newVal) this.options = []
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    async onSearch() {
      clearTimeout(this.debounceTimeout)
      const term = this.query
      if (this.requestedQuery === term) {
      }

      this.debounceTimeout = setTimeout(async () => {
        if (term.length < this.minQueryLength) {
          this.options = []
          this.showDropdown = false
          return
        }

        try {
          const result = await this.fetchOptions(term)
          this.options = result?.data ?? []
          this.showDropdown = true
          this.highlightedIndex = -1
          this.requestedQuery = term
        } catch (err) {
          // console.error('Greška u fetchOptions:', err)
          this.options = []
          this.showDropdown = false
        }
      }, 300)
    },
    selectOption(option) {
      this.query = option.name
      this.showDropdown = false
      this.highlightedIndex = -1
      this.$emit('input', option) // v-model = ceo objekat
    },
    highlightNext() {
      if (!this.showDropdown || !this.options.length) return
      this.highlightedIndex = (this.highlightedIndex + 1) % this.options.length
      this.scrollToSelected()
    },
    highlightPrev() {
      if (!this.showDropdown || !this.options.length) return
      this.highlightedIndex = (this.highlightedIndex - 1 + this.options.length) % this.options.length
      this.scrollToSelected()
    },
    selectHighlighted() {
      if (this.highlightedIndex >= 0 && this.highlightedIndex < this.options.length) {
        this.selectOption(this.options[this.highlightedIndex])
      }
    },
    handleClickOutside(event) {
      if (!this.$refs?.wrapper?.contains(event.target)) {
        this.showDropdown = false
      }
    },
    showSelected() {
      this.query = this.value?.name ?? ''
    },
    scrollToSelected() {
      this.$nextTick(() => {
        const el = this.$refs[`option-${this.highlightedIndex}`]?.[0]
        if (el) el.scrollIntoView({ block: 'nearest' })
      })
    },
  },
})

export default AutocompleteInput
</script>

<style scoped>
.autocomplete-wrapper {
  position: relative;
}

.autocomplete-list {
  position: absolute;
  z-index: 1000;
  background: white;
  list-style: none;
  padding: 0;
  margin: 0.3rem 0 0 0;
  width: 100%;
  border: 0.2rem solid #ccc;
  box-shadow: 0.2rem 0 0.5rem #fff;
  border-radius: 0.5rem;
  max-height: 240px;
  overflow-y: auto;
}

.autocomplete-list li {
  padding: 0.5rem 0.7rem;
  cursor: pointer;
}

.autocomplete-list li.highlighted {
  background-color: #007bff;
  color: white;
}
</style>
